/* -----------------Types--------------- */
import type { Confidence } from "@memorang/types/src/session/common";

import { Citations } from "@features/session-config/components/Citations";
import { AI_SUBMITTABLE_VARIANTS } from "@features/session/constants";
import useAIFeedbackStore from "@features/session/hooks/useAIFeedbackStore";
import { useAppTheme } from "@hooks/useAppTheme";
import type {
	Answer,
	Choice,
	ItemCriteria,
	ItemFeedback,
	OriginalItemTypes,
	OriginalStimulusTypes,
	RenderableItemTypes,
	UnifiedCitation,
} from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import type { BoxProps } from "@memorang/ui/src/components/Box";
import { useTranslation } from "react-i18next";
/* -----------------Third parties--------------- */
import * as Animatable from "react-native-animatable";
import type { MixedItemTag } from "types/tag";
import ExplanationContainer from "../../../containers/ExplanationContainer";
import AiModalHandle from "../AiModalHandle";
/* -----------------Components--------------- */
import AiPostAnswerHeader from "../AiPostAnswerHeader";
import PostAnswerStats from "../PostAnswerStats";
import { TagsList } from "../TagsList";

type Props = {
	choices: Choice[];
	timeTaken: number;
	maximumScore: number;
	confidence?: Confidence;
	correct?: boolean;
	currentQuestionAnswered?: boolean;
	showExplanation?: boolean;
	tags?: MixedItemTag[];
	showStats?: boolean;
	pointsEarnedStatValue?: string;
	itemType: RenderableItemTypes;
	itemId: number;
	numberOfWords?: number;
	originalItemType?: OriginalItemTypes;
	stimulusType?: OriginalStimulusTypes;
	isSummativeTest?: boolean;
	currentItemAnswer?: Answer;
	feedback?: ItemFeedback;
	itemCriteria?: ItemCriteria[];
	citations?: UnifiedCitation[];
};

const BorderWrapper = ({ children, ...props }: BoxProps) => {
	const theme = useAppTheme();
	return (
		<Box
			borderWidth={2}
			padding={16}
			borderColor={theme.colors.divider}
			borderRadius={theme.roundness * 3}
			gap={20}
			{...props}
		>
			{children}
		</Box>
	);
};
const PostAnswerModal = ({
	choices,
	timeTaken,
	maximumScore,
	confidence,
	correct,
	currentQuestionAnswered,
	showExplanation,
	tags,
	pointsEarnedStatValue,
	itemId,
	itemType,
	numberOfWords,
	originalItemType,
	stimulusType,
	isSummativeTest,
	currentItemAnswer,
	feedback,
	itemCriteria,
	citations,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const { t } = useTranslation();

	const isAiItemType = AI_SUBMITTABLE_VARIANTS.includes(itemType);

	const score = useAIFeedbackStore((store) =>
		currentItemAnswer
			? currentItemAnswer.score
			: store.aiResponse?.[itemId]?.score,
	);
	const subskillAssessments = useAIFeedbackStore(
		(store) =>
			currentItemAnswer?.aiResponse?.subskillAssessments ||
			store.aiResponse?.[itemId]?.subskillAssessments,
	);

	return (
		<Animatable.View
			useNativeDriver
			duration={300}
			animation="fadeIn"
			easing="ease-out"
			style={{
				paddingHorizontal: isSummativeTest ? 16 : 0,
			}}
		>
			{currentQuestionAnswered ? (
				<Box gap={24}>
					<BorderWrapper>
						<PostAnswerStats
							timeTaken={timeTaken}
							correct={correct}
							isAiItemType={isAiItemType}
							confidence={isSummativeTest ? undefined : confidence}
							numAnsweredStat={
								correct
									? t("session.answer.correct")
									: t("session.answer.incorrect")
							}
							pointsEarnedStatValue={pointsEarnedStatValue}
							numberOfWords={numberOfWords}
							itemType={itemType}
						/>
					</BorderWrapper>
					<BorderWrapper padding={0} gap={0}>
						{isAiItemType && (
							<AiModalHandle
								itemId={itemId}
								itemType={itemType}
								maximumScore={maximumScore}
							/>
						)}
						<Box paddingHorizontal={16} paddingVertical={16} gap={20}>
							{isAiItemType && (
								<AiPostAnswerHeader
									correct={correct}
									itemCriteria={itemCriteria}
									numAnsweredStat={
										correct
											? t("session.answer.correct")
											: t("session.answer.incorrect")
									}
									confidence={isSummativeTest ? undefined : confidence}
									pointsEarnedStatValue={pointsEarnedStatValue}
									score={score}
									subskillAssessments={subskillAssessments}
									totalScore={maximumScore}
									itemType={itemType}
									originalItemType={originalItemType}
									stimulusType={stimulusType}
								/>
							)}
							{showExplanation ? (
								<ExplanationContainer
									itemId={Number(itemId)}
									itemType={itemType}
									choices={choices}
									persistedAiFeedback={currentItemAnswer?.aiResponse}
									feedback={feedback}
								/>
							) : null}
							{tags && tags.length > 0 ? <TagsList tags={tags} /> : null}
						</Box>
						{citations?.length ? (
							<Box paddingBottom={16} paddingHorizontal={16}>
								<Citations citations={citations} />
							</Box>
						) : null}
					</BorderWrapper>
				</Box>
			) : null}
		</Animatable.View>
	);
};

export default PostAnswerModal;
